import React, { useState, useEffect, useRef } from 'react';
    import './apply-now.css';
    import { Container, Form, Button, Row, Col } from 'react-bootstrap';
    import axios from 'axios';
    import { useLocation } from 'react-router-dom';
    import { FaCheckCircle, FaMobileAlt, FaShieldAlt } from 'react-icons/fa';
    import ThankYou from './ThankYou';
    import {
      checkPanBlacklist,
      checkMultiVerticalPan,
      evaluateMultiVerticalPan,
      createEnquiryAutosave,
    } from '../config/taskApi';
    import {
      apiGetState,
      apiGetCity,
      apiGetPincodeByCityId,
      apiSendOtp,
      apiVerifyOtp,
      apiPanCustomer,
      apiSaveLead,
    } from '../config/apiGateway';

    const APPLY_LOAN_LS_KEY = 'dhanwalle_apply_loan_session';

    // Permanent local draft (no expiry). Cleared only if user clears browser data
    // or changes verified mobile (OTP reset).
    const DRAFT_FIELDS = [
      'loanAmount',
      'monthlyIncome',
      'firstName',
      'middleName',
      'lastName',
      'panNumber',
      'dob',
      'gender',
      'mobile',
      'altMobile',
      'email',
      'officialEmail',
      'obligations',
      // state / city / pincode — never restore from localStorage (user must select)
    ];

    const readLoanSession = () => {
      try {
        const raw = localStorage.getItem(APPLY_LOAN_LS_KEY);
        if (!raw) return null;
        const data = JSON.parse(raw);
        if (!data || typeof data !== 'object') return null;
        return data;
      } catch {
        return null;
      }
    };

    const writeLoanSession = (patch) => {
      try {
        const prev = readLoanSession() || {};
        const next = { ...prev, ...patch, updatedAt: Date.now() };
        // Never keep OTP / secrets in browser storage
        delete next.otp;
        delete next.security_code;
        delete next.apiKey;
        localStorage.setItem(APPLY_LOAN_LS_KEY, JSON.stringify(next));
        return next;
      } catch {
        return null;
      }
    };

    const clearLoanSession = () => {
      try {
        localStorage.removeItem(APPLY_LOAN_LS_KEY);
      } catch {
        /* ignore */
      }
    };

    const pickDraftFromForm = (form, extras = {}) => {
      const draft = {};
      DRAFT_FIELDS.forEach((key) => {
        draft[key] = form[key] ?? '';
      });
      return { ...draft, ...extras };
    };

    const ApplyNow = () => {

    const location = useLocation();
    const mobileFirst =
      location.pathname === '/apply-loan' || location.pathname === '/apply-now';
    const lastFetchedPanRef = useRef("");
    const storedSession = useRef(mobileFirst ? readLoanSession() : null);
    const boot = storedSession.current;
    const bootVerified = !!(boot?.otpVerified && boot?.mobile);
    const bootOtpPending = !!(
      !bootVerified &&
      boot?.otpSent &&
      boot?.mobile &&
      String(boot.mobile).replace(/\D/g, '').length === 10
    );
    const bootResendLeft = (() => {
      if (!bootOtpPending || !boot?.resendUntil) return 0;
      return Math.max(0, Math.ceil((Number(boot.resendUntil) - Date.now()) / 1000));
    })();

    const [formData, setFormData] = useState(() => {
      const base = {
        loanAmount: '',
        monthlyIncome: '',
        firstName: '',
        middleName: '',
        lastName: '',
        panNumber: '',
        dob: '',
        gender: '',
        mobile: '',
        altMobile: '',
        email: '',
        officialEmail: '',
        obligations: '',
        state: '',
        city: '',
        pincode: '',
        city_name: '',
        state_name: '',
        term_and_condition: false,
        notification_consent: false,
      };
      if (!boot) return base;
      const restored = { ...base };
      DRAFT_FIELDS.forEach((key) => {
        if (boot[key] != null && boot[key] !== '') restored[key] = boot[key];
      });
      if (boot.mobile) restored.mobile = boot.mobile;
      // Always leave state / city / pincode blank for manual select
      restored.state = '';
      restored.city = '';
      restored.pincode = '';
      restored.state_name = '';
      restored.city_name = '';
      return restored;
    });

    const [errors, setErrors] = useState({});
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [stateId, setStateId] = useState('');
    const [cityId, setCityId] = useState('');
    const [pincodeList, setPincodeList] = useState([]);
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [panExists, setPanExists] = useState(false);
    const [panInfo, setPanInfo] = useState('');
    const [otp, setOtp] = useState('');
    const [otpSent, setOtpSent] = useState(bootVerified || bootOtpPending);
    const [otpVerified, setOtpVerified] = useState(bootVerified);
    const [otpBusy, setOtpBusy] = useState(false);
    const [otpInfo, setOtpInfo] = useState(
      bootVerified
        ? 'Mobile already verified'
        : bootOtpPending
          ? 'OTP sent successfully'
          : ''
    );
    const [resendLeft, setResendLeft] = useState(bootResendLeft);

    const enquiryRef = useRef(null);
    const formSnapshotRef = useRef({});

    // Keep latest form snapshot for autosave / pagehide (no stale closures)
    formSnapshotRef.current = {
      formData,
      otpVerified,
      cityId,
      stateId,
    };

    if (!enquiryRef.current) {
      enquiryRef.current = createEnquiryAutosave({
        debounceMs: 650,
        getContext: () => formSnapshotRef.current,
      });
    }

    const resolveEnquiryStep = () => {
      const fd = formSnapshotRef.current.formData || {};
      const verified = !!formSnapshotRef.current.otpVerified;
      const cid = formSnapshotRef.current.cityId;
      const mobile = String(fd.mobile || '').replace(/\D/g, '').slice(-10);
      if (mobile.length !== 10) return null;
      const panOk = String(fd.panNumber || '').trim().length === 10;
      const detailsOk =
        !!fd.email &&
        !!fd.loanAmount &&
        (!!cid || !!fd.city || !!fd.city_name);
      if (detailsOk && verified) return 'details';
      if (panOk && verified) return 'pan';
      if (verified) return 'otp_verified';
      return 'mobile';
    };


    useEffect(() => {
        apiGetState()
        .then((res) => {
            const data = res.data || {};
            if (data.Status === 1) {
            setStates(data.Data);
            } else {
            alert(data.Message || 'Failed to fetch states');
            }
        })
        .catch(err => {
            console.error('Failed fetching states:', err);
            alert('Could not load states');
        });
    }, []);

    // Restore city list when draft has stateId (apply-loan)
    useEffect(() => {
      if (!mobileFirst || !stateId) return undefined;
      let cancelled = false;
      (async () => {
        try {
          const resp = await apiGetCity(stateId);
          if (!cancelled && resp.data?.Status === 1) {
            setCities(resp.data.Data);
          }
        } catch (err) {
          console.error('Failed restoring cities:', err);
        }
      })();
      return () => { cancelled = true; };
    }, [mobileFirst, stateId]);

    // Restore pincode list when draft has cityId
    useEffect(() => {
      if (!mobileFirst || !cityId) return undefined;
      let cancelled = false;
      (async () => {
        try {
          const resp = await apiGetPincodeByCityId(cityId);
          if (!cancelled && resp.data?.Status === 1) {
            setPincodeList(resp.data.Data);
          }
        } catch (err) {
          console.error('Failed restoring pincodes:', err);
        }
      })();
      return () => { cancelled = true; };
    }, [mobileFirst, cityId]);

    useEffect(() => {
      if (!mobileFirst || resendLeft <= 0) return undefined;
      const t = setInterval(() => setResendLeft((s) => Math.max(0, s - 1)), 1000);
      return () => clearInterval(t);
    }, [mobileFirst, resendLeft]);

    // LMS Enquires autosave — debounced; never blocks UX; skipped on final Submit
    useEffect(() => {
      if (!mobileFirst) return undefined;
      const step = resolveEnquiryStep();
      if (!step) return undefined;
      enquiryRef.current?.schedule(step);
      return undefined;
    }, [
      mobileFirst,
      formData.mobile,
      formData.firstName,
      formData.middleName,
      formData.lastName,
      formData.panNumber,
      formData.email,
      formData.loanAmount,
      formData.city,
      formData.city_name,
      cityId,
      otpVerified,
    ]);

    useEffect(() => {
      if (!mobileFirst) return undefined;
      const onLeave = () => {
        const step = resolveEnquiryStep() || 'pagehide';
        enquiryRef.current?.flushNow(step, { keepalive: true });
      };
      window.addEventListener('pagehide', onLeave);
      window.addEventListener('beforeunload', onLeave);
      return () => {
        window.removeEventListener('pagehide', onLeave);
        window.removeEventListener('beforeunload', onLeave);
        enquiryRef.current?.cancel();
      };
    }, [mobileFirst]);

    const resetMobileOtp = () => {
      // Keep draft fields; only drop OTP verification (e.g. mobile changed)
      writeLoanSession({
        otpVerified: false,
        otpSent: false,
        verifiedAt: null,
        resendUntil: null,
      });
      // Ensure OTP never remains in storage
      try {
        const prev = readLoanSession() || {};
        if ('otp' in prev) {
          delete prev.otp;
          localStorage.setItem(APPLY_LOAN_LS_KEY, JSON.stringify(prev));
        }
      } catch { /* ignore */ }
      setOtp('');
      setOtpSent(false);
      setOtpVerified(false);
      setOtpInfo('');
      setResendLeft(0);
      setErrors((prev) => ({ ...prev, otp: '' }));
    };

    // Permanent draft save for /apply-loan
    useEffect(() => {
      if (!mobileFirst) return undefined;
      const t = setTimeout(() => {
        const prev = readLoanSession() || {};
        writeLoanSession({
          ...pickDraftFromForm(formData),
          // Do not persist location — avoids stale selected UI + failed submit
          stateId: '',
          cityId: '',
          pincode: '',
          state_name: '',
          city_name: '',
          state: '',
          city: '',
          otpSent: !!otpSent,
          otpVerified: !!otpVerified,
          // Never persist OTP in localStorage (privacy)
          otp: undefined,
          verifiedAt: otpVerified ? (prev.verifiedAt || Date.now()) : null,
          resendUntil: otpSent && !otpVerified
            ? (prev.resendUntil || null)
            : null,
        });
        // Hard-remove otp key if older sessions stored it
        try {
          const cur = readLoanSession() || {};
          if ('otp' in cur) {
            delete cur.otp;
            localStorage.setItem(APPLY_LOAN_LS_KEY, JSON.stringify(cur));
          }
        } catch { /* ignore */ }
      }, 250);
      return () => clearTimeout(t);
    }, [mobileFirst, formData, otp, otpSent, otpVerified]);

   const handleChange = (e) => {
    let { id, value, type, checked } = e.target;
    let error = "";

    // PAN logic
    if (id === "panNumber") {
      value = value.toUpperCase().trim();

      if (value.length !== 10) {
        setPanInfo("");
      }

      if (lastFetchedPanRef.current && value !== lastFetchedPanRef.current) {
        setFormData(prev => ({
          ...prev,
          firstName: "", middleName: "", lastName: "", dob: "",
          gender: "",
          // apply-loan: keep verified mobile; do not kick user back to OTP
          ...(mobileFirst ? {} : { mobile: "" }),
          altMobile: "", email: "",
          officialEmail: "", obligations: "", pincode: "",
          city_name: "", state_name: "", term_and_condition: false
        }));

        setStateId("");
        setCityId("");
        lastFetchedPanRef.current = "";
        setPanInfo("");
      }

      const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]$/;

      if (value.length === 10) {
        if (!panRegex.test(value)) {
          error = "Invalid PAN format";
        } else {
          checkPanVal(value);
        }
      }
    }

    // Mobile validation
   if (id === "mobile" || id === "altMobile") {
        if (id === "mobile") {
          value = value.replace(/\D/g, '').slice(0, 10);
        }
        if (id === "altMobile") {
          value = value.replace(/\D/g, '').slice(0, 10);
        }

        // Step 1: Check if length is exactly 10
        if (value.length > 0 && value.length !== 10) {
            error = "Enter 10-digit number";
        }

        // Step 2: Check for duplicate number in real-time
        const otherField = id === "mobile" ? formData.altMobile : formData.mobile;
        if (value && value === otherField) {
            error = "Both numbers cannot be same";
        }

        // apply-loan only: reset OTP if mobile changes
        if (mobileFirst && id === "mobile") {
          if (value.length < 10 || value !== formData.mobile) {
            resetMobileOtp();
          }
        }
    }

    setFormData(prev => ({
      ...prev,
      [id]: type === "checkbox" ? checked : value
    }));

    setErrors(prev => ({
      ...prev,
      [id]: error
    }));
};

    // âœ… ðŸ‘‡ Paste this RIGHT BELOW handleChange
    const handleKeyPress = (e, fieldName) => {
    if (!/[0-9]/.test(e.key)) {
        e.preventDefault();
        setErrors(prev => ({
        ...prev,
        [fieldName]: 'Only digits are allowed',
        }));
    } else {
        // Clear the error if valid key is pressed
        setErrors(prev => ({
        ...prev,
        [fieldName]: '',
        }));
    }
    };


    const handleStateChange = async (e) => {
        const sid = e.target.value;
        const selectedState = states.find(s => s.state_id.toString() === sid);

        setFormData(prev => ({ ...prev, state: selectedState?.state || '', city: '', pincode: '' }));
        setStateId(sid);
        setCityId('');
        setCities([]);
        setPincodeList([]);
        setErrors(prev => ({ ...prev, state: '', city: '', pincode: '' }));

        if (!sid) return;

        try {
        const resp = await apiGetCity(sid);
        if (resp.data?.Status === 1) {
            setCities(resp.data.Data);
        } else {
            setCities([]);
        }
        } catch (err) {
        console.error('Failed fetching cities:', err);
        setCities([]);
        }
    };

    const handleCityChange = async (e) => {
        const cid = e.target.value;
        const selectedCity = cities.find(c => c.city_id.toString() === cid);

        setFormData(prev => ({
        ...prev,
        city: selectedCity?.city || '',
        pincode: ''
        }));
        setCityId(cid);
        setErrors(prev => ({ ...prev, city: '', pincode: '' }));

        try {
        const resp = await apiGetPincodeByCityId(cid);

        if (resp.data?.Status === 1) {
            setPincodeList(resp.data.Data);
        } else {
            setPincodeList([]);
        }
        } catch (err) {
        console.error('Failed fetching pincodes:', err);
        setPincodeList([]);
        }
    };


    const getFreshPanMessage = (apiMessage) => {
      const raw = (apiMessage || "").trim();
      const normalized = raw.toLowerCase();

      if (
        !normalized ||
        normalized.includes("customer not found") ||
        normalized.includes("not found")
      ) {
        return "No existing customer found. Please fill your details.";
      }

      return raw;
    };

    const checkPanVal = async (pan) => {
      try {
    
        // 1) Blacklist Check
        const blacklist = await checkPanBlacklist(pan);
    
        if (blacklist.status === true) {
          setPanExists(true);
          setPanInfo("");
    
          setErrors(prev => ({
            ...prev,
            panNumber:
              blacklist.message ||
              "This PAN is blacklisted. You cannot apply."
          }));
    
          lastFetchedPanRef.current = "";
          return;
        }
    
        // Multi-vertical PAN check temporarily disabled
        const multiCheck = {
          allowed: true
        };
    
        // 2) Existing customer check
        const resp = await apiPanCustomer(pan);
          const data = resp.data || {};

          if (data.status === true) {
            const d = data.data;

            // Some responses return status:true without customer payload — treat as fresh
            if (!d || typeof d !== 'object') {
              setPanExists(false);
              setPanInfo(
                getFreshPanMessage(data.message || data.Message) ||
                  'No existing customer found. Please fill your details.'
              );
              setErrors(prev => ({ ...prev, panNumber: "" }));
              lastFetchedPanRef.current = "";
              return;
            }

            lastFetchedPanRef.current = pan;

            setFormData(prev => ({
              ...prev,
              firstName: d.first_name || "",
              middleName: d.middle_name || "",
              lastName: d.sur_name || "",
              dob: d.dob || "",
              gender: d.gender || "",
              // apply-loan / apply-now OTP flow: never overwrite OTP-verified mobile
              mobile: mobileFirst ? prev.mobile : (d.mobile || ""),
              altMobile: d.alternate_mobile || "",
              email: d.email || "",
              officialEmail: d.alternate_email || "",
              obligations: d.obligations ? String(parseInt(d.obligations, 10)) : "",
              // Location: never autofill on mobile-first — user must select (avoids Required glitch)
              ...(mobileFirst
                ? {
                    pincode: "",
                    city_name: "",
                    state_name: "",
                    city: "",
                    state: "",
                  }
                : {
                    pincode: d.pincode || "",
                    city_name: d.city_name || "",
                    state_name: d.state_name || "",
                    city: d.city_name || "",
                    state: d.state_name || "",
                  }),
              term_and_condition: d.term_and_condition === "YES"
            }));

            if (mobileFirst) {
              setStateId("");
              setCityId("");
              setCities([]);
              setPincodeList([]);
            } else {
              setStateId(d.state_id || "");
              setCityId(d.city_id || "");
            }

            enquiryRef.current?.flushNow('pan');

            if (d.status === "CLOSED" || d.status === "REJECT" || d.status === "REJECTED") {
              setPanExists(false);
              setPanInfo(`Latest Status: ${d.status} (You can re-apply)`);
              setErrors(prev => ({ ...prev, panNumber: "" }));
            } else {
              setPanExists(true);
              setPanInfo("");
              setErrors(prev => ({
                ...prev,
                panNumber: `Your loan status is ${d.status}. Cannot re-apply.`
              }));
            }
          } else {
            const apiMsg = getFreshPanMessage(
              data.message || data.Message
            );

            setPanExists(false);
            setPanInfo(apiMsg);
            setErrors(prev => ({ ...prev, panNumber: "" }));
            lastFetchedPanRef.current = "";
            enquiryRef.current?.flushNow('pan');
          }
        } catch (err) {
          console.error("PAN Check Error:", err);
          const responseData = err.response?.data;
          if (responseData) {
            const apiMsg = getFreshPanMessage(
              responseData.message || responseData.Message
            );
            setPanExists(false);
            setPanInfo(apiMsg);
            setErrors(prev => ({ ...prev, panNumber: "" }));
            lastFetchedPanRef.current = "";
          } else {
            setPanExists(false);
            setPanInfo("");
            setErrors(prev => ({ ...prev, panNumber: "" }));
          }
        }
      };


  const sendMobileOtp = async () => {
    if (!mobileFirst || otpBusy) return;
    const cleanMobile = String(formData.mobile || '').replace(/\D/g, '').slice(-10);
    if (cleanMobile.length !== 10) {
      setErrors((prev) => ({ ...prev, mobile: 'Enter valid 10-digit mobile number' }));
      return;
    }

    setOtpBusy(true);
    setOtpInfo('');
    setErrors((prev) => ({ ...prev, mobile: '', otp: '' }));

    try {
      const resp = await apiSendOtp(cleanMobile);
      const data = resp.data || {};
      const apiRes = data.api_response || {};
      const ok =
        apiRes.return === true ||
        data.status === true ||
        data.Status === 1 ||
        data.success === true;

      const apiMsg = Array.isArray(apiRes.message)
        ? apiRes.message.join(' ')
        : apiRes.message || data.message || data.Message || '';

      if (ok) {
        const resendUntil = Date.now() + 60 * 1000;
        setOtpSent(true);
        setOtpVerified(false);
        setOtp('');
        setResendLeft(60);
        setOtpInfo(apiMsg || 'OTP sent successfully');
        setErrors((prev) => ({ ...prev, mobile: '' }));
        writeLoanSession({
          mobile: cleanMobile,
          otpSent: true,
          otpVerified: false,
          verifiedAt: null,
          resendUntil,
          ...pickDraftFromForm({ ...formData, mobile: cleanMobile }),
          stateId: '',
          cityId: '',
          pincode: '',
          state_name: '',
          city_name: '',
        });
        enquiryRef.current?.flushNow('mobile');
      } else {
        setErrors((prev) => ({
          ...prev,
          mobile: apiMsg || 'Failed to send OTP',
        }));
      }
    } catch (err) {
      console.error('sendOtp error:', err);
      setErrors((prev) => ({
        ...prev,
        mobile: 'Failed to send OTP. Please try again.',
      }));
    } finally {
      setOtpBusy(false);
    }
  };

  const verifyMobileOtp = async () => {
    if (!mobileFirst || otpBusy) return;
    const cleanMobile = String(formData.mobile || '').replace(/\D/g, '').slice(-10);
    const cleanOtp = String(otp || '').replace(/\D/g, '');

    if (cleanOtp.length < 4) {
      setErrors((prev) => ({ ...prev, otp: 'Enter valid OTP' }));
      return;
    }

    setOtpBusy(true);
    setErrors((prev) => ({ ...prev, otp: '' }));

    try {
      const resp = await apiVerifyOtp(cleanMobile, cleanOtp);
      const data = resp.data || {};
      const apiRes = data.api_response || {};
      const ok =
        apiRes.return === true ||
        data.status === true ||
        data.Status === 1 ||
        data.success === true;

      const apiMsg = Array.isArray(apiRes.message)
        ? apiRes.message.join(' ')
        : apiRes.message || data.message || data.Message || '';

      if (ok) {
        setOtpVerified(true);
        setOtp(''); // clear OTP from React memory / UI
        setOtpInfo(apiMsg || 'OTP verified successfully');
        setErrors((prev) => ({ ...prev, otp: '', mobile: '' }));
        writeLoanSession({
          mobile: cleanMobile,
          otpSent: true,
          otpVerified: true,
          verifiedAt: Date.now(),
          resendUntil: null,
          ...pickDraftFromForm({ ...formData, mobile: cleanMobile }),
          stateId: '',
          cityId: '',
          pincode: '',
          state_name: '',
          city_name: '',
        });
        // Immediate enquiry sync after OTP (do not wait for debounce)
        formSnapshotRef.current = {
          ...formSnapshotRef.current,
          formData: { ...formData, mobile: cleanMobile },
          otpVerified: true,
        };
        enquiryRef.current?.flushNow('otp_verified');
      } else {
        setOtpVerified(false);
        setErrors((prev) => ({
          ...prev,
          otp: apiMsg || 'Invalid OTP',
        }));
      }
    } catch (err) {
      console.error('verifyOtp error:', err);
      setOtpVerified(false);
      setErrors((prev) => ({
        ...prev,
        otp: 'OTP verification failed. Please try again.',
      }));
    } finally {
      setOtpBusy(false);
    }
  };

  const validate = () => {
    const newErrors = {};
    const onlyDigits = /^\d+$/;

    if (!formData.loanAmount) newErrors.loanAmount = 'Required';
    if (!formData.monthlyIncome) {
        newErrors.monthlyIncome = 'Required';
    } else if (Number(formData.monthlyIncome) < 30000) {
        newErrors.monthlyIncome = 'Monthly income must be ₹30,000 or above';
    }
    if (!formData.firstName) newErrors.firstName = 'Required';
    if (!formData.panNumber) newErrors.panNumber = 'Required';
    if (!formData.dob) newErrors.dob = 'Required';
    if (!formData.gender) newErrors.gender = 'Required';

    // Personal Mobile Validation
    if (!formData.mobile) {
        newErrors.mobile = 'Required';
    } else if (!onlyDigits.test(formData.mobile) || formData.mobile.length !== 10) {
        newErrors.mobile = 'Enter valid 10-digit mobile number';
    }

    // apply-loan / apply-now: OTP must be verified (OTP itself is not stored after verify)
    if (mobileFirst && !otpVerified) {
      newErrors.otp = 'Please verify mobile OTP to continue';
    }

    // Alternate Mobile Validation (Required, 10 digits, and Unique)
    if (!formData.altMobile) {
        newErrors.altMobile = 'Alternate mobile is required';
    } else if (!onlyDigits.test(formData.altMobile) || formData.altMobile.length !== 10) {
        newErrors.altMobile = 'Enter valid 10-digit alternate number';
    } else if (formData.altMobile === formData.mobile) {
        newErrors.altMobile = 'Alternate number must be different from primary mobile';
    }

    if (!formData.email) newErrors.email = 'Required';
    if (!formData.state || (mobileFirst && !stateId)) newErrors.state = 'Required';
    if (!formData.city || (mobileFirst && !cityId)) newErrors.city = 'Required';
    if (!formData.pincode) newErrors.pincode = 'Required';

    if (!formData.term_and_condition) {
        newErrors.term_and_condition = 'Please accept the terms';
    }

    if (!formData.notification_consent) {
        newErrors.notification_consent = 'Please authorise notifications to continue';
    }

    return newErrors;
};

    const handleSubmit = async (e) => {
        e.preventDefault();
        const formErrors = validate();
        
        if (Object.keys(formErrors).length) {
            setErrors(formErrors);
            return;
        }

        // Final Submit → LEAD-NEW only (never saveEnquiry here)
        enquiryRef.current?.setLeadSubmitting(true);
        enquiryRef.current?.cancel();
    
        const payload = {
            loan_amount: formData.loanAmount,
            monthly_income: formData.monthlyIncome,
            first_name: formData.firstName,
            middle_name: formData.middleName || null,
            sur_name: formData.lastName || null,
            pan: formData.panNumber,
            dob: formData.dob,
            gender: formData.gender.toUpperCase(),
            mobile: formData.mobile,
            alternate_mobile: formData.altMobile || null,
            // OTP intentionally omitted — injected server-side from verify session
            email_personal: formData.email,
            email_official: formData.officialEmail || null,
            obligations: formData.obligations || null,
            state_id: stateId,
            city: cityId,
            pin: formData.pincode,
            term_and_condition: formData.term_and_condition ? 'YES' : 'NO', 
            ip: '106.219.215.60',
            coordinates: null,
            source: 'Dhanwalle',
            lead_data_source_id: 38,
            utm_source: '',
            utm_campaign: null,
            privacy_policy_consent: formData.term_and_condition ? 'YES' : 'NO',
            notification_consent: formData.notification_consent ? 'YES' : 'NO',
        };
    
        try {
            const resp = await apiSaveLead(payload);
    
            if (resp.data && resp.data.Status === 1) {
                clearLoanSession();
                setOtp('');
                setOtpVerified(false);
                setOtpSent(false);
                setFormSubmitted(true);
                const trackingLink = "https://dhanwalle.com/check"; 
                const smsVariables = `${formData.firstName}| ${trackingLink}|`;
                const smsUrl = `https://www.fast2sms.com/dev/bulkV2?authorization=CNHnel3FiDU6P0GXQh7yVuMzER5cI4pogAJsBS9kxKbZaw1Y2LN8cOnHPz3G2QdRbeom7UJsfKviLyMZ&route=dlt&sender_id=VMCRED&message=207219&variables_values=${encodeURIComponent(smsVariables)}&flash=0&numbers=${encodeURIComponent(formData.mobile)}`;
    
                try {
                    await axios.get(smsUrl);
                } catch (smsErr) {
                    console.error("SMS API error");
                }
            } else {
                enquiryRef.current?.setLeadSubmitting(false);
                const msg = String(resp.data?.Message || resp.data?.message || 'Submit failed');
                const plainTextMessage = msg.replace(/<[^>]+>/g, "\n").trim();
                if (
                  resp.status === 401 ||
                  /OTP session/i.test(plainTextMessage)
                ) {
                  resetMobileOtp();
                  alert(plainTextMessage || 'Please verify mobile OTP again');
                  return;
                }
                alert(plainTextMessage || "Submit failed");
            }
        } catch (err) {
            enquiryRef.current?.setLeadSubmitting(false);
            console.error('Submit error:', err);
            alert('Submit failed');
        }
    };

    if (formSubmitted) {
        return <ThankYou />;
    }

    return (
        <div className={`apply-now-wrapper ${mobileFirst ? 'apply-loan-flow' : ''}`}>
        <Container className={`apply-now-form-container ${mobileFirst ? 'apply-loan-card' : ''} ${mobileFirst && !otpVerified ? 'is-otp-step' : ''}`}>
            <h2 className="form-heading">
              {mobileFirst && !otpVerified ? 'Mobile Verification' : 'Apply Now'}
            </h2>
            <Form onSubmit={handleSubmit} noValidate className="apply-now-form">

            {mobileFirst && (
            <div className={`apply-mobile-step ${otpVerified ? 'is-verified' : ''}`}>
              {!otpVerified ? (
                <div className="loan-otp-panel">
                  <div className="loan-otp-icon">
                    <FaMobileAlt />
                  </div>
                  <h3 className="loan-otp-title">Verify your mobile</h3>
                  <p className="loan-otp-sub">
                    Enter your 10-digit mobile number. We will send a one-time password to continue your application.
                  </p>

                  <Form.Group controlId="mobile" className="mb-0 apply-form-field loan-otp-field">
                    <Form.Label>Mobile Number <span className="required">*</span></Form.Label>
                    <Form.Control
                      type="tel"
                      maxLength="10"
                      value={formData.mobile}
                      onChange={handleChange}
                      onKeyPress={(e) => handleKeyPress(e, 'mobile')}
                      isInvalid={!!errors.mobile}
                      placeholder="e.g. 9876543210"
                      disabled={otpVerified}
                    />
                    <div className="field-feedback-slot field-feedback-slot--error">
                      {errors.mobile || ''}
                    </div>
                  </Form.Group>

                  {!otpSent ? (
                    <Button
                      type="button"
                      className="loan-otp-primary"
                      onClick={sendMobileOtp}
                      disabled={otpBusy || formData.mobile.length !== 10}
                    >
                      {otpBusy ? 'Sending OTP…' : 'Send OTP'}
                    </Button>
                  ) : (
                    <>
                      <div className="loan-otp-sent">
                        <FaShieldAlt /> OTP sent to {formData.mobile}
                        {otpInfo ? ` · ${otpInfo}` : ''}
                      </div>

                      <Form.Group controlId="otp" className="mb-0 apply-form-field loan-otp-field">
                        <Form.Label>Enter OTP <span className="required">*</span></Form.Label>
                        <Form.Control
                          type="tel"
                          maxLength="6"
                          value={otp}
                          onChange={(e) => {
                            setOtp(e.target.value.replace(/\D/g, '').slice(0, 6));
                            setErrors((prev) => ({ ...prev, otp: '' }));
                          }}
                          isInvalid={!!errors.otp}
                          placeholder="6-digit OTP"
                          className="loan-otp-input"
                        />
                        <div className="field-feedback-slot field-feedback-slot--error">
                          {errors.otp || ''}
                        </div>
                      </Form.Group>

                      <div className="loan-otp-actions">
                        <Button
                          type="button"
                          className="loan-otp-primary"
                          onClick={verifyMobileOtp}
                          disabled={otpBusy || otp.length < 4}
                        >
                          {otpBusy ? 'Verifying…' : 'Verify & Continue'}
                        </Button>
                        <button
                          type="button"
                          className="loan-otp-link"
                          onClick={sendMobileOtp}
                          disabled={otpBusy || resendLeft > 0}
                        >
                          {resendLeft > 0 ? `Resend in ${resendLeft}s` : 'Resend OTP'}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div className="loan-verified-bar">
                  <FaCheckCircle />
                  <div>
                    <strong>Mobile verified</strong>
                    <span>{formData.mobile}</span>
                  </div>
                </div>
              )}
            </div>
            )}

            {(!mobileFirst || otpVerified) && (
            <>
            {mobileFirst && (
            <>
            <Row className="apply-form-row g-2 apply-form-grid">
                <Col md={6}>
                    <Form.Group controlId="altMobile" className="mb-0 apply-form-field">
                        <Form.Label>Alternate Mobile <span className="required">*</span></Form.Label>
                        <Form.Control
                            type="tel"
                            maxLength="10"
                            value={formData.altMobile}
                            onChange={handleChange}
                            onKeyPress={(e) => handleKeyPress(e, 'altMobile')}
                            isInvalid={!!errors.altMobile}
                            placeholder="Enter alternate mobile"
                        />
                        <div className="field-feedback-slot field-feedback-slot--error">
                            {errors.altMobile || '\u00A0'}
                        </div>
                    </Form.Group>
                </Col>
                <Col md={6}>
                <Form.Group controlId="panNumber" className="mb-0 apply-form-field pan-field-group">
                    <Form.Label>PAN Number <span className="required">*</span></Form.Label>
                    <Form.Control
                    type="text"
                    maxLength="10"
                    value={formData.panNumber}
                    onChange={handleChange}
                    isInvalid={!!errors.panNumber}
                    placeholder="Enter your pan number"
                    />
                    <div className={`field-feedback-slot ${errors.panNumber ? 'field-feedback-slot--error' : 'field-feedback-slot--info'}`}>
                        {errors.panNumber || panInfo || '\u00A0'}
                    </div>
                </Form.Group>
                </Col>
            </Row>

            <Row className="apply-form-row g-2 apply-form-grid">
                <Col md={6}>
                <Form.Group controlId="loanAmount" className="mb-0 apply-form-field">
                    <Form.Label>Loan Amount <span className="required">*</span></Form.Label>
                    <Form.Control
                    type="number"
                    value={formData.loanAmount}
                    onChange={handleChange}
                    isInvalid={!!errors.loanAmount}
                    />
                    <div className="field-feedback-slot field-feedback-slot--error">
                        {errors.loanAmount || '\u00A0'}
                    </div>
                </Form.Group>
                </Col>
                <Col md={6}>
                <Form.Group controlId="monthlyIncome" className="mb-0 apply-form-field">
                    <Form.Label>Monthly Income <span className="required">*</span></Form.Label>
                    <Form.Control
                    type="number"
                    value={formData.monthlyIncome}
                    onChange={handleChange}
                    isInvalid={!!errors.monthlyIncome}
                    />
                    <div className="field-feedback-slot field-feedback-slot--error">
                        {errors.monthlyIncome || '\u00A0'}
                    </div>
                </Form.Group>
                </Col>
            </Row>

            <Row className="apply-form-row g-2 apply-form-grid">
                <Col md={6}>
                <Form.Group controlId="firstName" className="mb-0 apply-form-field">
                    <Form.Label>First Name <span className="required">*</span></Form.Label>
                    <Form.Control
                    type="text"
                    value={formData.firstName}
                    onChange={handleChange}
                    isInvalid={!!errors.firstName}
                    />
                    <div className="field-feedback-slot field-feedback-slot--error">
                        {errors.firstName || '\u00A0'}
                    </div>
                </Form.Group>
                </Col>
                <Col md={6}>
                <Form.Group controlId="middleName" className="mb-0 apply-form-field">
                    <Form.Label>Middle Name</Form.Label>
                    <Form.Control
                    type="text"
                    value={formData.middleName}
                    onChange={handleChange}
                    />
                    <div className="field-feedback-slot">{'\u00A0'}</div>
                </Form.Group>
                </Col>
            </Row>

            <Row className="apply-form-row g-2 apply-form-grid">
                <Col md={6}>
                <Form.Group controlId="lastName" className="mb-0 apply-form-field">
                    <Form.Label>Surname</Form.Label>
                    <Form.Control
                    type="text"
                    value={formData.lastName}
                    onChange={handleChange}
                    />
                    <div className="field-feedback-slot">{'\u00A0'}</div>
                </Form.Group>
                </Col>
                <Col md={6}>
                <Form.Group controlId="dob" className="mb-0 apply-form-field">
                    <Form.Label>Date of Birth <span className="required">*</span></Form.Label>
                    <Form.Control
                    type="date"
                    value={formData.dob}
                    onChange={handleChange}
                    isInvalid={!!errors.dob}
                    />
                    <div className="field-feedback-slot field-feedback-slot--error">
                        {errors.dob || '\u00A0'}
                    </div>
                </Form.Group>
                </Col>
            </Row>

            <Row className="apply-form-row g-2 apply-form-grid">
                <Col md={6}>
                <Form.Group controlId="gender" className="mb-0 apply-form-field">
                    <Form.Label>Gender <span className="required">*</span></Form.Label>
                    <Form.Select
                    value={formData.gender}
                    onChange={handleChange}
                    isInvalid={!!errors.gender}
                    >
                    <option value="">Select Gender</option>
                    <option value="MALE">MALE</option>
                    <option value="FEMALE">FEMALE</option>
                    <option value="OTHER">OTHER</option>
                    </Form.Select>
                    <div className="field-feedback-slot field-feedback-slot--error">
                        {errors.gender || '\u00A0'}
                    </div>
                </Form.Group>
                </Col>
                <Col md={6}>
                <Form.Group controlId="email" className="mb-0 apply-form-field">
                    <Form.Label>Email <span className="required">*</span></Form.Label>
                    <Form.Control
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    isInvalid={!!errors.email}
                    />
                    <div className="field-feedback-slot field-feedback-slot--error">
                        {errors.email || '\u00A0'}
                    </div>
                </Form.Group>
                </Col>
            </Row>

            <Row className="apply-form-row g-2 apply-form-grid">
                <Col md={6}>
                <Form.Group controlId="officialEmail" className="mb-0 apply-form-field">
                    <Form.Label>Official Email</Form.Label>
                    <Form.Control
                    type="email"
                    value={formData.officialEmail}
                    onChange={handleChange}
                    />
                    <div className="field-feedback-slot">{'\u00A0'}</div>
                </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group controlId="obligations" className="mb-0 apply-form-field">
                        <Form.Label>Obligations</Form.Label>
                        <Form.Control
                            type="text"
                            maxLength="7"
                            value={formData.obligations}
                            onChange={handleChange}
                            onKeyPress={(e) => handleKeyPress(e, 'obligations')}
                            isInvalid={!!errors.obligations}
                        />
                        <div className="field-feedback-slot field-feedback-slot--error">
                            {errors.obligations || '\u00A0'}
                        </div>
                    </Form.Group>
                </Col>
            </Row>
            </>
            )}

            {!mobileFirst && (
            <>
            <Row className="apply-form-row g-3">
                <Col md={6}>
                <Form.Group controlId="panNumber" className="mb-0 apply-form-field pan-field-group">
                    <Form.Label>PAN Number <span className="required">*</span></Form.Label>
                    <Form.Control
                    type="text"
                    maxLength="10"
                    value={formData.panNumber}
                    onChange={handleChange}
                    isInvalid={!!errors.panNumber}
                    placeholder="Enter your pan number"
                    />
                    {panInfo && <div className="pan-info-message">{panInfo}</div>}
                    <Form.Control.Feedback type="invalid">{errors.panNumber}</Form.Control.Feedback>
                </Form.Group>
                </Col>
                <Col md={6}>
                <Form.Group controlId="loanAmount" className="mb-0 apply-form-field">
                    <Form.Label>Loan Amount <span className="required">*</span></Form.Label>
                    <Form.Control
                    type="number"
                    value={formData.loanAmount}
                    onChange={handleChange}
                    isInvalid={!!errors.loanAmount}
                    />
                    <Form.Control.Feedback type="invalid">{errors.loanAmount}</Form.Control.Feedback>
                </Form.Group>
                </Col>
            </Row>

            <Row className="apply-form-row g-3">
                <Col md={6}>
                <Form.Group controlId="monthlyIncome" className="mb-0 apply-form-field">
                    <Form.Label>Monthly Income <span className="required">*</span></Form.Label>
                    <Form.Control
                    type="number"
                    value={formData.monthlyIncome}
                    onChange={handleChange}
                    isInvalid={!!errors.monthlyIncome}
                    />
                    <Form.Control.Feedback type="invalid">{errors.monthlyIncome}</Form.Control.Feedback>
                </Form.Group>
                </Col>
                <Col md={6}>
                <Form.Group controlId="firstName" className="mb-0 apply-form-field">
                    <Form.Label>First Name <span className="required">*</span></Form.Label>
                    <Form.Control
                    type="text"
                    value={formData.firstName}
                    onChange={handleChange}
                    isInvalid={!!errors.firstName}
                    />
                    <Form.Control.Feedback type="invalid">{errors.firstName}</Form.Control.Feedback>
                </Form.Group>
                </Col>
            </Row>

            <Row className="apply-form-row g-3">
                <Col md={6}>
                <Form.Group controlId="middleName" className="mb-0 apply-form-field">
                    <Form.Label>Middle Name</Form.Label>
                    <Form.Control
                    type="text"
                    value={formData.middleName}
                    onChange={handleChange}
                    />
                </Form.Group>
                </Col>
                <Col md={6}>
                <Form.Group controlId="lastName" className="mb-0 apply-form-field">
                    <Form.Label>Surname</Form.Label>
                    <Form.Control
                    type="text"
                    value={formData.lastName}
                    onChange={handleChange}
                    />
                </Form.Group>
                </Col>
            </Row>

            <Row className="apply-form-row g-3">
                <Col md={6}>
                <Form.Group controlId="dob" className="mb-0 apply-form-field">
                    <Form.Label>Date of Birth <span className="required">*</span></Form.Label>
                    <Form.Control
                    type="date"
                    value={formData.dob}
                    onChange={handleChange}
                    isInvalid={!!errors.dob}
                    />
                    <Form.Control.Feedback type="invalid">{errors.dob}</Form.Control.Feedback>
                </Form.Group>
                </Col>
                <Col md={6}>
                <Form.Group controlId="gender" className="mb-0 apply-form-field">
                    <Form.Label>Gender <span className="required">*</span></Form.Label>
                    <Form.Select
                    value={formData.gender}
                    onChange={handleChange}
                    isInvalid={!!errors.gender}
                    >
                    <option value="">Select Gender</option>
                    <option value="MALE">MALE</option>
                    <option value="FEMALE">FEMALE</option>
                    <option value="OTHER">OTHER</option>
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">{errors.gender}</Form.Control.Feedback>
                </Form.Group>
                </Col>
            </Row>

            <Row className="apply-form-row g-3">
                <Col md={6}>
                    <Form.Group controlId="mobile" className="mb-0 apply-form-field">
                        <Form.Label>Mobile <span className="required">*</span></Form.Label>
                        <Form.Control
                            type="tel"
                            maxLength="10"
                            value={formData.mobile}
                            onChange={handleChange}
                            onKeyPress={(e) => handleKeyPress(e, 'mobile')}
                            isInvalid={!!errors.mobile}
                        />
                        <div className="field-feedback-slot field-feedback-slot--error">
                            {errors.mobile || ''}
                        </div>
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group controlId="altMobile" className="mb-0 apply-form-field">
                        <Form.Label>Alternate Mobile <span className="required">*</span></Form.Label>
                        <Form.Control
                            type="tel"
                            maxLength="10"
                            value={formData.altMobile}
                            onChange={handleChange}
                            onKeyPress={(e) => handleKeyPress(e, 'altMobile')}
                            isInvalid={!!errors.altMobile}
                        />
                        <div className="field-feedback-slot field-feedback-slot--error">
                            {errors.altMobile || ''}
                        </div>
                    </Form.Group>
                </Col>
            </Row>

            <Row className="apply-form-row g-3">
                <Col md={6}>
                <Form.Group controlId="email" className="mb-0 apply-form-field">
                    <Form.Label>Email <span className="required">*</span></Form.Label>
                    <Form.Control
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    isInvalid={!!errors.email}
                    />
                    <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
                </Form.Group>
                </Col>
                <Col md={6}>
                <Form.Group controlId="officialEmail" className="mb-0 apply-form-field">
                    <Form.Label>Official Email</Form.Label>
                    <Form.Control
                    type="email"
                    value={formData.officialEmail}
                    onChange={handleChange}
                    />
                </Form.Group>
                </Col>
            </Row>

            </>
            )}

            <Row className={`apply-form-row ${mobileFirst ? 'g-2 apply-form-grid' : 'g-3'}`}>
                {!mobileFirst && (
                <Col md={6}>
                    <Form.Group controlId="obligations" className="mb-0 apply-form-field">
                        <Form.Label>Obligations</Form.Label>
                        <Form.Control
                            type="text"
                            maxLength="7"
                            value={formData.obligations}
                            onChange={handleChange}
                            onKeyPress={(e) => handleKeyPress(e, 'obligations')}
                            isInvalid={!!errors.obligations}
                        />
                        <div className="field-feedback-slot field-feedback-slot--error">
                            {errors.obligations || ''}
                        </div>
                    </Form.Group>
                </Col>
                )}
                <Col md={6}>
                    <Form.Group controlId="state" className="mb-0 apply-form-field">
                        <Form.Label>State <span className="required">*</span></Form.Label>
                        {!mobileFirst && formData.state_name !== '' ? (
                        <>
                            <Form.Control type="hidden" name="state_id" value={formData.state_id} />
                            <Form.Control type="text" value={formData.state_name} readOnly />
                        </>
                        ) : (
                        <Form.Select value={stateId} onChange={handleStateChange} isInvalid={!!errors.state}>
                            <option value="">Select State</option>
                            {states.map(s => (
                            <option key={s.state_id} value={s.state_id}>{s.state}</option>
                            ))}
                        </Form.Select>
                        )}
                        {mobileFirst ? (
                          <div className="field-feedback-slot field-feedback-slot--error">
                            {errors.state || '\u00A0'}
                          </div>
                        ) : (
                          <Form.Control.Feedback type="invalid">{errors.state}</Form.Control.Feedback>
                        )}
                    </Form.Group>
                </Col>
                {mobileFirst && (
                <Col md={6}>
                    <Form.Group controlId="city" className="mb-0 apply-form-field">
                        <Form.Label>City <span className="required">*</span></Form.Label>
                        <Form.Select value={cityId} onChange={handleCityChange} isInvalid={!!errors.city}>
                            <option value="">Select City</option>
                            {cities.map(city => (
                            <option key={city.city_id} value={city.city_id}>{city.city}</option>
                            ))}
                        </Form.Select>
                        <div className="field-feedback-slot field-feedback-slot--error">
                          {errors.city || '\u00A0'}
                        </div>
                    </Form.Group>
                </Col>
                )}
            </Row>

            <Row className={`apply-form-row ${mobileFirst ? 'g-2 apply-form-grid' : 'g-3'}`}>
                {!mobileFirst && (
                <Col md={6}>
                    <Form.Group controlId="city" className="mb-0 apply-form-field">
                        <Form.Label>City</Form.Label>
                        {formData.city_name !== '' ? (
                        <>
                            <Form.Control type="hidden" name="city_id" value={formData.city_id} />
                            <Form.Control type="text" value={formData.city_name} readOnly />
                        </>
                        ) : (
                        <Form.Select value={cityId} onChange={handleCityChange}>
                            <option value="">Select City</option>
                            {cities.map(city => (
                            <option key={city.city_id} value={city.city_id}>{city.city}</option>
                            ))}
                        </Form.Select>
                        )}
                    </Form.Group>
                </Col>
                )}
                <Col md={6}>
                    <Form.Group controlId="pincode" className="mb-0 apply-form-field">
                        <Form.Label>Pincode <span className="required">*</span></Form.Label>
                        {!mobileFirst && formData.pincode !== '' ? (
                            <Form.Control type="text" value={formData.pincode} readOnly />
                        ) : (
                            <Form.Select
                            value={formData.pincode}
                            onChange={handleChange}
                            disabled={!cityId || pincodeList.length === 0}
                            isInvalid={!!errors.pincode}
                            >
                            <option value="">Select Pincode</option>
                            {pincodeList.map(pin => (
                                <option key={pin.m_pincode_id} value={pin.m_pincode_value}>
                                {pin.m_pincode_value}
                                </option>
                            ))}
                            </Form.Select>
                        )}
                        <div className="field-feedback-slot field-feedback-slot--error">
                            {errors.pincode || (mobileFirst ? '\u00A0' : '')}
                        </div>
                    </Form.Group>
                </Col>
            </Row>

            <Form.Group controlId="term_and_condition" className="mb-3 terms-check">
  <Form.Check
    type="checkbox"
    label={
      <>
        <a href="terms-conditions" target="_blank">Terms & Conditions</a> <span className="required">*</span>
      </>
    }
    checked={formData.term_and_condition}
    onChange={handleChange} // <-- Yeh change kiya
    isInvalid={!!errors.term_and_condition}
  />
  {errors.term_and_condition && (
    <div className="text-danger" style={{ fontSize: '0.8rem' }}>
      {errors.term_and_condition}
    </div>
  )}
</Form.Group>

<div className="declaration-text mb-3">
  I hereby declare that all the above information provided by me is true, correct, and verified to the best of my knowledge. <span className="required">*</span>
</div>

<Form.Group controlId="notification_consent" className="mb-3 terms-check">
  <Form.Check
    type="checkbox"
    label={
      <>
        I hereby authorise to send notifications via SMS, Email, RCS and others as per{' '}
        <a href="terms-conditions" target="_blank" rel="noopener noreferrer">Terms of Service</a>
        /
        <a href="privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
        <span className="required"> *</span>
      </>
    }
    checked={formData.notification_consent}
    onChange={handleChange}
    isInvalid={!!errors.notification_consent}
  />
  {errors.notification_consent && (
    <div className="text-danger" style={{ fontSize: '0.8rem' }}>
      {errors.notification_consent}
    </div>
  )}
</Form.Group>

<div className="submit-btn-wrapper">

<Button 
    type="submit" 
    className="apply-submit-btn"
    disabled={
      (panExists === true && !errors.panNumber?.includes("REJECT")) ||
      (mobileFirst && !otpVerified)
    }
>
    Submit
</Button>
</div>
            </>
            )}

            </Form>
        </Container>
        </div>
    );
    };

    export default ApplyNow;